/** Shared date/period-end formatting for the admin ledger views.
 *  Previously duplicated between OrgsPage, AuditPage and OrgDetailPage. */

/** Short date, e.g. "15/07/2026" — used for created/joined/period-end columns. */
export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('vi-VN');
}

/** Date + time, e.g. "15/07/2026, 09:30:00" — used for audit-log timestamps. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('vi-VN');
}

/** Matches `RENEWAL_LEAD_DAYS` in `apps/api/src/services/subscriptionBilling.ts`,
 *  the window in which the backend issues a renewal invoice and the dashboard
 *  nudges the teacher. Kept as a local copy rather than an import: admin is a
 *  separate SPA that must not pull in API internals, and this is a display
 *  threshold, not a billing decision. Past-due dates always count. */
const ADMIN_ATTENTION_LEAD_DAYS = 7;

/** True when a plan's `currentPeriodEnd` is already past or within the
 *  attention lead window — the one honest use of the ochre accent in admin. */
export function isPeriodEndNear(iso: string | null): boolean {
  if (!iso) return false;
  const leadMs = ADMIN_ATTENTION_LEAD_DAYS * 24 * 60 * 60 * 1000;
  return new Date(iso).getTime() - Date.now() <= leadMs;
}

const VND_FORMATTER = new Intl.NumberFormat('vi-VN');

/** VND amount, e.g. "1.250.000 đ" — used for BankHub deposit figures. */
export function formatVnd(n: number): string {
  return `${VND_FORMATTER.format(n)} đ`;
}

/** §2.3: a true instant (Promotion.startsAt/endsAt and the like) is formatted
 *  in an explicit `Asia/Ho_Chi_Minh`, never the viewer's own browser timezone. */
const VN_TIME_ZONE = 'Asia/Ho_Chi_Minh';

/** True instant → VN-time date + time, e.g. "15/07/2026 09:30". */
export function formatVnDateTime(iso: string): string {
  return new Intl.DateTimeFormat('vi-VN', {
    timeZone: VN_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** True instant → `<input type="datetime-local">` value, in VN wall time
 *  (`sv-SE` gives a stable "YYYY-MM-DD HH:mm:ss" ordering to slice, not a
 *  locale choice for display). */
export function toVnDatetimeLocal(iso: string): string {
  const s = new Date(iso).toLocaleString('sv-SE', { timeZone: VN_TIME_ZONE });
  return s.slice(0, 16).replace(' ', 'T');
}

/** `<input type="datetime-local">` value, entered as VN wall time → ISO
 *  instant. VN has a fixed +07:00 offset with no DST, so appending it
 *  directly is exact regardless of the viewer's own timezone. */
export function fromVnDatetimeLocal(value: string): string {
  return new Date(`${value}:00+07:00`).toISOString();
}

export type PromotionRunState = 'ACTIVE' | 'OFF' | 'UPCOMING' | 'EXPIRED';

/** A promotion's running state derives from three stored fields, never a
 *  fifth status column — `active` is the admin's on/off switch, `startsAt`/
 *  `endsAt` are the window. A plain function (not a component) so the
 *  `Date.now()` read doesn't trip the React Compiler's purity check the way
 *  an inline call in a component body would. */
export function promotionRunState(active: boolean, startsAt: string, endsAt: string): PromotionRunState {
  const now = Date.now();
  if (!active) return 'OFF';
  if (now < new Date(startsAt).getTime()) return 'UPCOMING';
  if (now > new Date(endsAt).getTime()) return 'EXPIRED';
  return 'ACTIVE';
}
